if (!customElements.get('ombre-kith-hero')) {
  class OmbreKithHero extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('[data-on-kith-header]');
      this.menuButton = this.querySelector('[data-on-kith-menu]');
      this.menuPanel = this.querySelector('[data-on-kith-menu-panel]');
      this.menuDialog = this.querySelector('[data-on-kith-menu-dialog]');
      this.menuCloseButtons = this.querySelectorAll('[data-on-kith-menu-close]');
      this.desktopShell = this.querySelector('[data-on-kith-desktop-shell]');
      this.desktopDrawer = this.querySelector('[data-on-kith-desktop-drawer]');
      this.desktopBackdrop = this.querySelector('[data-on-kith-desktop-backdrop]');
      this.desktopTriggers = Array.from(this.querySelectorAll('[data-on-kith-desktop-trigger]'));
      this.desktopMenuPanels = Array.from(this.querySelectorAll('[data-on-kith-desktop-menu-panel]'));
      this.desktopCategoryTriggers = Array.from(this.querySelectorAll('[data-on-kith-desktop-category]'));
      this.desktopCategoryPanels = Array.from(this.querySelectorAll('[data-on-kith-desktop-panel]'));
      this.cartButton = this.querySelector('[data-on-kith-cart]');
      this.scrollTicking = false;

      document.body.classList.add('ombre-kith-hero-active');

      this.handleMenuClick = (event) => this.openMenu(event);
      this.handleMenuClose = (event) => this.closeMenu(event);
      this.handleDesktopBackdrop = (event) => this.closeDesktopDrawer(event);
      this.handleCartClick = (event) => this.openCart(event);
      this.handleScroll = () => this.requestHeaderSync();
      this.handleResize = () => {
        if (window.innerWidth < 750 && this.desktopDrawerOpen) {
          this.closeDesktopDrawer(null, false);
        }
      };
      this.handleKeydown = (event) => {
        if (event.key === 'Escape' && this.desktopDrawerOpen) {
          this.closeDesktopDrawer(event);
        } else if (event.key === 'Escape' && this.menuOpen) {
          this.closeMenu(event);
        }
      };

      this.desktopTriggerHandlers = this.desktopTriggers.map((trigger) => {
        const handler = (event) => this.openDesktopDrawer(trigger, event);
        trigger.addEventListener('click', handler);
        return { trigger, handler };
      });
      this.desktopCategoryHandlers = this.desktopCategoryTriggers.map((trigger) => {
        const handler = (event) => this.toggleDesktopCategory(trigger, event);
        trigger.addEventListener('click', handler);
        return { trigger, handler };
      });

      this.menuButton?.addEventListener('click', this.handleMenuClick);
      this.menuCloseButtons?.forEach((button) => button.addEventListener('click', this.handleMenuClose));
      this.desktopBackdrop?.addEventListener('click', this.handleDesktopBackdrop);
      this.cartButton?.addEventListener('click', this.handleCartClick);
      this.cartButton?.setAttribute('aria-haspopup', 'dialog');
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });
      window.addEventListener('keydown', this.handleKeydown);
      this.syncHeader();
    }

    disconnectedCallback() {
      document.body.classList.remove('ombre-kith-hero-active');
      document.body.classList.remove('ombre-kith-menu-open');
      document.body.classList.remove('ombre-kith-desktop-nav-open');
      this.menuButton?.removeEventListener?.('click', this.handleMenuClick);
      this.menuCloseButtons?.forEach((button) => button.removeEventListener('click', this.handleMenuClose));
      this.desktopTriggerHandlers?.forEach(({ trigger, handler }) => trigger.removeEventListener('click', handler));
      this.desktopCategoryHandlers?.forEach(({ trigger, handler }) => trigger.removeEventListener('click', handler));
      this.desktopBackdrop?.removeEventListener?.('click', this.handleDesktopBackdrop);
      this.cartButton?.removeEventListener?.('click', this.handleCartClick);
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('keydown', this.handleKeydown);
    }

    openMenu(event) {
      if (this.menuPanel) {
        event.preventDefault();
        this.menuPanel.hidden = false;
        this.menuOpen = true;
        window.requestAnimationFrame(() => this.menuPanel.classList.add('is-open'));
        document.body.classList.add('ombre-kith-menu-open');
        this.menuButton?.setAttribute('aria-expanded', 'true');
        this.menuDialog?.focus();
        return;
      }

      const drawer = document.querySelector('header-drawer');
      const summary = drawer?.querySelector?.('summary');
      if (!drawer || !summary) return;

      event.preventDefault();
      drawer?.setMenuOpener?.(this.menuButton);

      if (typeof drawer?.openFromOpener === 'function') {
        drawer.openFromOpener(this.menuButton);
      } else {
        summary?.click();
      }
    }

    closeMenu(event) {
      if (!this.menuPanel) return;

      event?.preventDefault?.();
      this.menuOpen = false;
      this.menuPanel.classList.remove('is-open');
      document.body.classList.remove('ombre-kith-menu-open');
      this.menuButton?.setAttribute('aria-expanded', 'false');
      setTimeout(() => {
        if (!this.menuOpen) this.menuPanel.hidden = true;
      }, 220);
      this.menuButton?.focus?.();
    }

    openDesktopDrawer(trigger, event) {
      if (!this.desktopShell || !trigger) return;

      const menuKey = trigger.dataset?.onKithDesktopTrigger;
      const activePanel = this.desktopMenuPanels.find(
        (panel) => panel.dataset?.onKithDesktopMenuPanel === menuKey,
      );
      if (!activePanel) return;

      event?.preventDefault?.();
      if (this.activeDesktopTrigger !== trigger) this.resetDesktopCategories();
      this.desktopMenuPanels.forEach((panel) => {
        panel.hidden = panel !== activePanel;
      });
      this.desktopTriggers.forEach((candidate) => {
        candidate.setAttribute('aria-expanded', candidate === trigger ? 'true' : 'false');
      });

      this.activeDesktopTrigger = trigger;
      this.desktopShell.hidden = false;
      this.desktopDrawerOpen = true;
      window.requestAnimationFrame(() => this.desktopShell.classList.add('is-open'));
      document.body.classList.add('ombre-kith-desktop-nav-open');
      this.header?.classList?.add?.('is-menu-open');
    }

    closeDesktopDrawer(event, restoreFocus = true) {
      if (!this.desktopShell) return;

      event?.preventDefault?.();
      this.desktopDrawerOpen = false;
      this.desktopShell.classList.remove('is-open');
      document.body.classList.remove('ombre-kith-desktop-nav-open');
      this.header?.classList?.remove?.('is-menu-open');
      this.desktopTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      this.resetDesktopCategories();

      const triggerToRestore = this.activeDesktopTrigger;
      setTimeout(() => {
        if (!this.desktopDrawerOpen) this.desktopShell.hidden = true;
      }, 220);
      if (restoreFocus) triggerToRestore?.focus?.();
    }

    toggleDesktopCategory(trigger, event) {
      event?.preventDefault?.();
      const panelId = trigger.getAttribute('aria-controls');
      const activePanel = this.desktopCategoryPanels.find((panel) => panel.id === panelId);
      if (!activePanel) return;

      const shouldExpand = trigger.getAttribute('aria-expanded') !== 'true';
      this.resetDesktopCategories();
      if (shouldExpand) {
        trigger.setAttribute('aria-expanded', 'true');
        activePanel.hidden = false;
      }
    }

    resetDesktopCategories() {
      this.desktopCategoryTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      this.desktopCategoryPanels.forEach((panel) => {
        panel.hidden = true;
      });
    }

    openCart(event) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;

      event.preventDefault();
      event.stopPropagation();
      drawer.open(this.cartButton);
    }

    requestHeaderSync() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        this.syncHeader();
        this.scrollTicking = false;
      });
    }

    syncHeader() {
      const scrollTop = Math.max(0, window.pageYOffset || 0, document.documentElement.scrollTop || 0);
      this.header?.classList.toggle('is-scrolled', scrollTop > 2);
    }
  }

  customElements.define('ombre-kith-hero', OmbreKithHero);
}
